/**
 * What the top-bar My Actions pill shows, from the outcome of `GET /my-actions/badge`.
 *
 * Three states, because a count is only worth showing when it is known to be complete:
 *   ok       — every source was read; `count` is the total.
 *   partial  — a source could not be read (`unavailable_sources`); `count` is what the
 *              others hold, so the pill shows it with a "+" rather than as the total.
 *   unknown  — the request failed or the answer was unusable; there is no count.
 * The pill used to show nothing in the last two, which reads as "nothing assigned".
 */

export const BADGE_STATES = Object.freeze({
  OK: "ok",
  PARTIAL: "partial",
  UNKNOWN: "unknown",
});

const UNKNOWN = Object.freeze({ state: BADGE_STATES.UNKNOWN, count: 0, urgent: 0, label: "?" });

/** The pill's text: "" for nothing to show, "?" when unknown, "5+" when partial. */
function labelFor(state, count) {
  if (state === BADGE_STATES.UNKNOWN) return "?";
  if (count > 99) return "99+";
  if (state === BADGE_STATES.PARTIAL) return count > 0 ? `${count}+` : "?";
  return count > 0 ? String(count) : "";
}

/**
 * @param {PromiseSettledResult<{data?: object}>} settled the badge request, as
 *   `Promise.allSettled` reports it
 * @returns {{state: string, count: number, urgent: number, label: string}}
 */
export function myActionsBadge(settled) {
  // a rejected result has no `value`, so a failed request lands here with the
  // unusable answers: it has no counts to show
  const data = settled?.value?.data;
  const counts = data?.counts;
  if (!counts || typeof counts.total !== "number") return UNKNOWN;

  const unavailable = Array.isArray(data.unavailable_sources) ? data.unavailable_sources : [];
  const state = unavailable.length > 0 ? BADGE_STATES.PARTIAL : BADGE_STATES.OK;
  const count = counts.total;
  const urgent = (counts.overdue || 0) + (counts.due_today || 0);
  return { state, count, urgent, label: labelFor(state, count) };
}
